import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserDetailsModal from '../components/UserDetailsModal';
import AssignCourseModal from '../components/AssignCourseModal';
import userService from '../services/userService';

const ManageUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // Changed to 5 to show pagination with fewer users
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAssignCourseModalOpen, setIsAssignCourseModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Fetch users on component mount and when pagination changes
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await userService.getAllUsers(currentPage, pageSize);

      // Check if response is paginated
      if (data.results && data.count !== undefined) {
        // Paginated response
        setUsers(data.results);
        setFilteredUsers(data.results);
        setTotalCount(data.count);
        setTotalPages(data.total_pages);
      } else {
        // Non-paginated response (backward compatibility)
        const userArray = Array.isArray(data) ? data : [];
        setUsers(userArray);
        setFilteredUsers(userArray);
        setTotalCount(userArray.length);
        // Calculate total pages based on array length
        setTotalPages(Math.ceil(userArray.length / pageSize));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch users');
      if (err.response?.status === 403) {
        setError('You do not have permission to view this page');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (user) => {
    try {
      const detailedUser = await userService.getUserDetails(user.id);
      setSelectedUser(detailedUser);
      setIsDetailsModalOpen(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to fetch user details');
    }
  };

  const handleToggleStatus = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} ${user.username}?`)) {
      try {
        await userService.toggleUserStatus(user.id);
        alert(`User ${action}d successfully`);
        fetchUsers(); // Refresh the list
      } catch (err) {
        alert(err.response?.data?.error || `Failed to ${action} user`);
      }
    }
  };

  const handleChangeRole = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    if (window.confirm(`Are you sure you want to change ${user.username}'s role to ${newRole}?`)) {
      try {
        await userService.changeUserRole(user.id, newRole);
        alert('User role changed successfully');
        fetchUsers(); // Refresh the list
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to change user role');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to permanently delete ${user.username}? This action cannot be undone.`)) {
      try {
        await userService.deleteUser(user.id);
        alert('User deleted successfully');
        fetchUsers(); // Refresh the list
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const handleAssignCourse = (user) => {
    setSelectedUser(user);
    setIsAssignCourseModalOpen(true);
  };

  const handleAssignCourseSubmit = async (userId, courseId) => {
    try {
      const response = await userService.assignCourse(userId, courseId);
      alert(response.message || 'Course assigned successfully');
      fetchUsers(); // Refresh the list
    } catch (err) {
      throw err; // Let the modal handle the error display
    }
  };

  const handleRemoveCourse = async (userId, courseId, courseName) => {
    if (window.confirm(`Are you sure you want to remove "${courseName}" from this user?`)) {
      try {
        const response = await userService.removeCourse(userId, courseId);
        alert(response.message || 'Course removed successfully');
        // Refresh the user details
        const detailedUser = await userService.getUserDetails(userId);
        setSelectedUser(detailedUser);
        fetchUsers(); // Refresh the main list
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to remove course');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <div className="py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-3xl font-bold text-slate-900">Manage Users</h1>

              {/* Right Side: Search Bar and Button */}
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap text-sm font-medium"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-gray-600">View and manage all registered users</p>
              {searchTerm && (
                <p className="text-sm text-gray-600">
                  Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Users Table */}
          {!isLoading && !error && (
            <div className="bg-white rounded-lg shadow mb-48" style={{ overflow: 'visible' }}>
              {filteredUsers.length > 0 ? (
                <div style={{ overflow: 'visible' }}>
                  <table className="min-w-full divide-y divide-gray-200" style={{ overflow: 'visible' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Courses
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" style={{ overflow: 'visible' }}>
                      {filteredUsers.map((user, index) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.username}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                              {user.full_name && (
                                <div className="text-xs text-gray-400">{user.full_name}</div>
                              )}
                              {user.phone && (
                                <div className="text-xs text-gray-400">📞 {user.phone}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.role === 'admin'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.date_joined)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.enrolled_courses_count || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              {/* Quick View Button */}
                              <button
                                onClick={() => handleViewDetails(user)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>

                              {/* Actions Dropdown */}
                              <div className="relative">
                                <button
                                  onClick={() => setOpenDropdownId(openDropdownId === user.id ? null : user.id)}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="More Actions"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                  </svg>
                                </button>

                                {/* Dropdown Menu */}
                                {openDropdownId === user.id && (
                                  <>
                                    {/* Backdrop to close dropdown */}
                                    <div
                                      className="fixed inset-0 z-10"
                                      onClick={() => setOpenDropdownId(null)}
                                    />
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                                      <button
                                        onClick={() => {
                                          handleToggleStatus(user);
                                          setOpenDropdownId(null);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                          user.is_active ? 'text-orange-700' : 'text-green-700'
                                        }`}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          {user.is_active ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                          ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          )}
                                        </svg>
                                        {user.is_active ? 'Deactivate User' : 'Activate User'}
                                      </button>

                                      <button
                                        onClick={() => {
                                          handleChangeRole(user);
                                          setOpenDropdownId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-purple-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                        </svg>
                                        Change to {user.role === 'admin' ? 'User' : 'Admin'}
                                      </button>

                                      <button
                                        onClick={() => {
                                          handleAssignCourse(user);
                                          setOpenDropdownId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Assign Course
                                      </button>

                                      <div className="border-t border-gray-200 my-1"></div>

                                      <button
                                        onClick={() => {
                                          handleDeleteUser(user);
                                          setOpenDropdownId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete User
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-600">
                  {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                </div>
              )}
            </div>
          )}

          {/* Pagination and Page Size Controls - Always visible when users exist */}
          {!isLoading && !error && totalCount > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
              </div>

              {/* Pagination Controls - Always visible */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || totalPages <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {/* First Page - only show if we're far from it */}
                  {totalPages > 1 && currentPage > 3 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={totalPages <= 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        1
                      </button>
                      {currentPage > 4 && <span className="px-2 text-gray-500">...</span>}
                    </>
                  )}

                  {/* Previous Page - only show if exists */}
                  {totalPages > 1 && currentPage > 1 && (
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={totalPages <= 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {currentPage - 1}
                    </button>
                  )}

                  {/* Current Page - Always show */}
                  <button
                    disabled={totalPages <= 1}
                    className={`px-3 py-2 border-2 rounded-lg text-sm font-medium ${
                      totalPages <= 1
                        ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed'
                        : 'border-blue-600 bg-blue-50 text-blue-600'
                    }`}
                  >
                    {currentPage}
                  </button>

                  {/* Next Page - only show if exists */}
                  {totalPages > 1 && currentPage < totalPages && (
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={totalPages <= 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {currentPage + 1}
                    </button>
                  )}

                  {/* Last Page - only show if we're far from it */}
                  {totalPages > 1 && currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={totalPages <= 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>

              {/* Page Size Selector - Always visible */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Modals */}
      <UserDetailsModal
        user={selectedUser}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedUser(null);
        }}
        onRemoveCourse={handleRemoveCourse}
      />

      <AssignCourseModal
        user={selectedUser}
        isOpen={isAssignCourseModalOpen}
        onClose={() => {
          setIsAssignCourseModalOpen(false);
          setSelectedUser(null);
        }}
        onAssign={handleAssignCourseSubmit}
      />
    </div>
  );
};

export default ManageUsers;
